import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { apiRequest, formatCurrency, getImageUrl } from "../../../core/common/api";
import AppImage from "../../../shared/AppImage";
import { paymentMethodLabel, orderStatusTone, isGatewayMethod } from "../../../shared/Utils";
import MetricCard from "../../../shared/MetricCard";
import PaginationBar from "../../../shared/PaginationBar";
import ResetButton from "../../../shared/ResetButton";
import useTableSearch from "../../../shared/useTableSearch";

const ORDER_STATUSES = [
  "Processing",
  "Shipped",
  "Delivered",
  "Cancelled",
  "Return Requested",
  "Return Approved",
  "Return Rejected",
  "Refunded",
];

const getAvailableStatusOptions = (status?: string) => {
  const options: Record<string, string[]> = {
    Processing: ["Processing", "Shipped", "Delivered", "Cancelled"],
    Shipped: ["Shipped", "Delivered", "Cancelled"],
    Delivered: ["Delivered", "Return Requested"],
    "Return Requested": [
      "Return Requested",
      "Return Approved",
      "Return Rejected",
    ],
    "Return Approved": ["Return Approved", "Refunded"],
    Cancelled: ["Cancelled", "Refunded"],
  };

  return options[status || ""] || [status || "-"];
};

const formatDate = (date?: string) => {
  if (!date) return "-";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(date));
};

const OrdersList = () => {
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState<any[]>([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalOrders: 0,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    status: "all",
    payment: "all",
    sort: "newest",
  });
  const searchTerm = useTableSearch(filters.search);

  const fetchOrders = useCallback(
    async (page = 1) => {
      setIsLoading(true);

      try {
        const params = new URLSearchParams({
          search: searchTerm,
          status: filters.status,
          payment: filters.payment,
          sort: filters.sort,
          page: String(page),
          limit: String(pagination.limit),
        });

        const result = await apiRequest(`/orders?${params.toString()}`, "GET");

        if (result.success) {
          setOrders(result.orders || []);
          setTotalAmount(result.totalAmount || 0);
          if (result.pagination) {
            setPagination({
              page: result.pagination.page,
              limit: result.pagination.limit,
              totalPages: result.pagination.totalPages,
              totalOrders: result.totalOrders || (result.orders || []).length,
              total: result.pagination.total ?? result.totalOrders ?? (result.orders || []).length,
            });
          }
        }
      } catch (error: any) {
        Swal.fire({
          title: error.message || "Unable to load orders",
          icon: "error",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [filters.status, filters.payment, filters.sort, searchTerm, pagination.limit],
  );

  useEffect(() => {
    fetchOrders(1);
  }, [fetchOrders]);

  const currentOrders = orders;

  const resetFilters = () => {
    setFilters({
      search: "",
      status: "all",
      payment: "all",
      sort: "newest",
    });
    setPagination((old) => ({ ...old, page: 1 }));
  };

  const updateOrderStatus = async (order: any, status: string) => {
    if (order.orderStatus === status) return;

    if (status === "Cancelled") {
      await cancelOrder(order);
      return;
    }

    if (status === "Return Approved" || status === "Return Rejected") {
      await processReturn(order, status === "Return Approved" ? "approve" : "reject");
      return;
    }

    if (status === "Refunded") {
      await refundOrder(order);
      return;
    }

    let reason = "";
    if (status === "Return Requested") {
      const response = await Swal.fire({
        title: "Request return?",
        input: "textarea",
        inputLabel: "Return reason",
        inputPlaceholder: "Write reason...",
        showCancelButton: true,
        confirmButtonText: "Request Return",
      });

      if (!response.isConfirmed) return;
      reason = response.value || "Requested by admin";
    }

    try {
      const result = await apiRequest(`/orders/${order._id}`, "PUT", {
        status,
        ...(reason && { reason }),
      });

      if (!result.success) {
        throw new Error(result.message || "Status update failed");
      }

      await fetchOrders();
      Swal.fire({
        title: "Order Updated",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error: any) {
      Swal.fire("Update Failed", error.message || "Status update failed", "error");
    }
  };

  const cancelOrder = async (order: any) => {
    const response = await Swal.fire({
      title: "Cancel order?",
      input: "textarea",
      inputLabel: "Cancellation reason",
      inputPlaceholder: "Write reason...",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Cancel Order",
      confirmButtonColor: "#ef4444",
    });

    if (!response.isConfirmed) return;

    try {
      const result = await apiRequest(`/orders/${order._id}/cancel`, "PUT", {
        reason: response.value || "Cancelled by admin",
      });

      if (!result.success) {
        throw new Error(result.message || "Order cancel failed");
      }

      await fetchOrders();
      Swal.fire({
        title: "Order Cancelled",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error: any) {
      Swal.fire("Cancel Failed", error.message || "Order cancel failed", "error");
    }
  };

  const processReturn = async (order: any, action: "approve" | "reject") => {
    const response = await Swal.fire({
      title: action === "approve" ? "Approve return?" : "Reject return?",
      input: "textarea",
      inputLabel: "Admin note",
      inputPlaceholder: "Write note...",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: action === "approve" ? "Approve" : "Reject",
    });

    if (!response.isConfirmed) return;

    try {
      const result = await apiRequest(`/orders/${order._id}/return`, "PUT", {
        action,
        adminNote: response.value || "",
      });

      if (!result.success) {
        throw new Error(result.message || "Return update failed");
      }

      await fetchOrders();
      Swal.fire({
        title: "Return Updated",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error: any) {
      Swal.fire("Return Failed", error.message || "Return update failed", "error");
    }
  };

  const refundOrder = async (order: any) => {
    const response = await Swal.fire({
      title: "Process refund?",
      html: `<p style="margin:0 0 12px;color:#64748b">Full refund amount: ${formatCurrency((isGatewayMethod(order.paymentInfo?.method) ? order.paymentInfo?.amount : order.orderStatus === "Cancelled" ? Number(order.walletPay?.eWallet || 0) + Number(order.walletPay?.repurchaseFund || 0) : order.totalPrice))}</p>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Refund",
    });

    if (!response.isConfirmed) return;

    try {
      const result = await apiRequest(`/orders/${order._id}/refund`, "PUT", {
        status: "Completed",
      });

      if (!result.success) {
        throw new Error(result.message || "Refund update failed");
      }

      await fetchOrders();
      Swal.fire({
        title: "Refund Updated",
        text: result.message || "Refund status updated",
        icon: result.order?.refundInfo?.status === "Completed" ? "success" : "info",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error: any) {
      Swal.fire("Refund Failed", error.message || "Refund update failed", "error");
    }
  };

  const metrics = [
    {
      label: "Total Orders",
      value: orders.length,
      icon: "📦",
      tone: "orange",
    },
    {
      label: "Processing Orders",
      value: orders.filter((item) => item.orderStatus === "Processing").length,
      icon: "🧾",
      tone: "blue",
    },
    {
      label: "Cancelled Orders",
      value: orders.filter((item) => item.orderStatus === "Cancelled").length,
      icon: "⛔",
      tone: "purple",
    },
    {
      label: "Total Revenue",
      value: formatCurrency(totalAmount),
      icon: "💰",
      tone: "pink",
    },
  ];

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">Overview</div>
          <h1 className="page-title">
            All <em>Orders</em>
          </h1>
          <div className="page-sub">
            Track all orders, payment status, delivery status and refunds.
          </div>
        </div>
      </div>

      <section className="metrics-grid">
        {metrics.map((metric) => (
          <MetricCard key={metric.label} metric={metric} />
        ))}
      </section>

      <div className="toolbar">
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            value={filters.search}
            onChange={(e) => {
              setFilters({ ...filters, search: e.target.value });
            }}
            placeholder="Search order, customer, phone, product..."
          />
        </div>

        <select
          className="filter-select"
          value={filters.status}
          onChange={(e) => {
            setFilters({ ...filters, status: e.target.value });
            setPagination((old) => ({ ...old, page: 1 }));
          }}
        >
          <option value="all">All Order Status</option>
          {ORDER_STATUSES.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>

        <select
          className="filter-select"
          value={filters.payment}
          onChange={(e) => {
            setFilters({ ...filters, payment: e.target.value });
            setPagination((old) => ({ ...old, page: 1 }));
          }}
        >
          <option value="all">All Payments</option>
          <option value="Completed">Completed</option>
          <option value="Pending">Pending</option>
          <option value="Failed">Failed</option>
        </select>

        <select
          className="filter-select"
          value={filters.sort}
          onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="amountHigh">Amount High</option>
          <option value="amountLow">Amount Low</option>
        </select>

        <ResetButton onClick={resetFilters} />
      </div>

      <section className="table-card">
        <div className="table-head">
          <h2>Orders List</h2>
          <p>Manage Orders</p>
        </div>

        <div className="table-scroll">
          <table>
          <thead>
            <tr>
              <th>Order</th>
              <th>Customer</th>
              <th>Items</th>
              <th>Payment Status</th>
              <th>Total</th>
              <th>Status</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="empty-state">
                  Loading Orders...
                </td>
              </tr>
            ) : currentOrders.length === 0 ? (
              <tr>
                <td colSpan={8} className="empty-state">
                  No Orders found.
                </td>
              </tr>
            ) : (
              currentOrders.map((order) => {
                const firstItem = order.orderItems?.[0];
                const statusOptions = getAvailableStatusOptions(order.orderStatus);
                const canUpdate = statusOptions.length > 1;
                const canCancel = ![
                  "Delivered",
                  "Cancelled",
                  "Refunded",
                ].includes(order.orderStatus);
                const canProcessReturn =
                  order.orderStatus === "Return Requested" &&
                  order.returnRequest?.status === "Requested";
                const canRefund = ["Cancelled", "Return Approved"].includes(
                  order.orderStatus,
                );

                return (
                  <tr key={order._id}>
                    <td>
                      <Link
                        to={`/orders/${order._id}`}
                        className="table-link"
                        title="View Order Details"
                      >
                        {order.orderCode || "-"}
                      </Link>
                      <small className="table-muted">
                        {paymentMethodLabel(order.paymentInfo?.method)}
                      </small>
                    </td>
                    <td>
                      <strong>
                        {order.user?.name ||
                          order.shippingInfo?.fullName ||
                          "Customer"}
                      </strong>
                      <small className="table-muted">
                        {order.user?.memberId || order.shippingInfo?.phoneNo || "-"}
                      </small>
                    </td>
                    <td>
                      <div className="img-name-cell">
                        <AppImage
                          src={getImageUrl(firstItem?.image)}
                          alt={firstItem?.name || "Order item"}
                          crossOrigin="anonymous"
                        />
                        <div>
                          <strong>{firstItem?.name || "Order Items"}</strong>
                          <small>
                            {(order.orderItems || []).length} item(s), Qty{" "}
                            {(order.orderItems || []).reduce(
                              (sum: number, item: any) =>
                                sum + Number(item.quantity || 0),
                              0,
                            )}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`status-badge ${
                          order.paymentInfo?.status === "Completed"
                            ? "success"
                            : order.paymentInfo?.status === "Failed"
                              ? "danger"
                              : "warning"
                        }`}
                      >
                        {order.paymentInfo?.status || "-"}
                      </span>
                    </td>
                    <td>
                      <strong>{formatCurrency(order.totalPrice)}</strong>
                      {Number(order.coupon?.discountAmount || 0) > 0 && (
                        <small className="table-muted">
                          Coupon {formatCurrency(order.coupon.discountAmount)}
                        </small>
                      )}
                    </td>
                    <td>
                      {canUpdate ? (
                        <select
                          className="table-status-select"
                          value={order.orderStatus}
                          onChange={(event) =>
                            updateOrderStatus(order, event.target.value)
                          }
                        >
                          {statusOptions.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className={`status-badge ${orderStatusTone(
                            order.orderStatus,
                          )}`}
                        >
                          {order.orderStatus}
                        </span>
                      )}
                    </td>
                    <td>{formatDate(order.createdAt)}</td>
                    <td>
                      <div className="actions-btns order-actions">
                        <Link
                          to={`/orders/${order._id}`}
                          title="View Details"
                          className="action-button edit"
                        >
                          👁️
                        </Link>

                        {canCancel && (
                          <button
                            type="button"
                            onClick={() => cancelOrder(order)}
                            title="Cancel order"
                            className="action-button delete"
                          >
                            ✖
                          </button>
                        )}

                        {canProcessReturn && (
                          <>
                            <button
                              type="button"
                              onClick={() => processReturn(order, "approve")}
                              title="Approve return"
                              className="action-button edit"
                            >
                              ✓
                            </button>
                            <button
                              type="button"
                              onClick={() => processReturn(order, "reject")}
                              title="Reject return"
                              className="action-button delete"
                            >
                              ↯
                            </button>
                          </>
                        )}

                        {canRefund && (
                          <button
                            type="button"
                            onClick={() => refundOrder(order)}
                            title="Refund order"
                            className="action-button edit"
                          >
                            ₹
                          </button>
                        )}

                        
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          </table>
        </div>

        <PaginationBar
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.total || pagination.totalOrders}
          totalPages={pagination.totalPages}
          itemLabel="orders"
          onPageChange={fetchOrders}
          onLimitChange={(limit) =>
            setPagination((prev) => ({
              ...prev,
              page: 1,
              limit,
            }))
          }
        />
      </section>
    </>
  );
};

export default OrdersList;
